import { useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Paper } from '@mui/material';
import { ShowBase, useRecordContext, useRedirect } from 'react-admin';
import { useParams } from 'react-router-dom';
import { Quote } from '../types';
import { QuoteShowContent } from './QuoteShow';
import { QuoteEdit } from './QuoteEdit';

/**
 * Back-to-Account button shown above the quote card.
 * Reads account_id from the quote record to build the link.
 */
const BackToAccountButton = () => {
    const record = useRecordContext<Quote>();
    const redirect = useRedirect();

    if (!record?.account_id) return null;

    return (
        <Box sx={{ mb: 1 }}>
            <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => redirect('show', 'accounts', record.account_id)}
                sx={{ textTransform: 'none' }}
            >
                Back to Account
            </Button>
        </Box>
    );
};

/**
 * Standalone full-page quote view.
 * Accessible at /quotes/:id/page.
 * Reuses the same QuoteShowContent as the dialog version.
 */
export const QuoteShowPage = () => {
    const { id } = useParams<{ id: string }>();
    const redirect = useRedirect();
    const [editOpen, setEditOpen] = useState(false);

    const handleClose = () => {
        redirect('list', 'quotes');
    };

    if (!id) return null;

    return (
        <Box
            sx={{
                mx: 'auto',
                my: 3,
                px: 2,
                overflowX: 'auto',
            }}
        >
            <ShowBase resource="quotes" id={id}>
                <BackToAccountButton />
                <Paper elevation={1} sx={{ overflowX: 'auto' }}>
                    <QuoteShowContent
                        handleClose={handleClose}
                        onEdit={() => setEditOpen(true)}
                    />
                </Paper>
            </ShowBase>
            <QuoteEdit
                open={editOpen}
                id={id}
                onClose={() => setEditOpen(false)}
            />
        </Box>
    );
};
